// ═══════════════════════════════════════════════
//   GACHA — display-case machine
// ═══════════════════════════════════════════════

const {S, CONFIG, ALIENS, GACHA_POOL, BUNNYMEALS, BUNNYMEAL_POOL, SFX, addPts, toast, scheduleSave} = window;

// Rarity display config — kept as plain strings so they work inside JS.
// Visual styling lives in gacha.css via the .rare modifier class on slots/dots/recents.
const RARITY_RC = {
    common:   { stars: '★',   label: 'COMMON' },
    uncommon: { stars: '★★',  label: 'UNCOMMON' },
    rare:     { stars: '★★★', label: '✦ RARE ✦' },
};

// Items shown in the aliens 3×3 display case (toys first, then aliens)
const ALIEN_DISPLAY_ITEMS = [
    ...GACHA_POOL.filter(e => e.type === 'toy').map(e => ({
        id: e.id, src: e.img, name: e.name, rarity: 'common', type: 'toy',
    })),
    ...GACHA_POOL.filter(e => e.type === 'alien').map(e => ({
        id: e.id, src: ALIENS[e.id].img, name: ALIENS[e.id].name, rarity: ALIENS[e.id].rarity, type: 'alien',
    })),
];

// Items shown in the bunnymeals display case (populated when entries exist)
const BUNNYMEAL_DISPLAY_ITEMS = BUNNYMEAL_POOL.filter(e => e.type === 'bunnymeal').map(e => ({
    id: e.id, src: BUNNYMEALS[e.id]?.img || '', name: BUNNYMEALS[e.id]?.name || e.id,
    rarity: BUNNYMEALS[e.id]?.rarity || 'common', type: 'bunnymeal',
}));

// Banner config — controls which pool/display is active
const BANNERS = {
    aliens: {
        title: '✦ ALIEN GACHA ✦',
        pool: GACHA_POOL,
        displayItems: ALIEN_DISPLAY_ITEMS,
        emptyMsg: 'no pulls yet',
    },
    bunnymeals: {
        title: '✦ BUNNYMEALS ✦',
        pool: BUNNYMEAL_POOL,
        displayItems: BUNNYMEAL_DISPLAY_ITEMS,
        emptyMsg: 'coming soon!',
    },
};

let activeBanner = 'aliens';
let isPulling = false;
let pullHistory = { aliens: [], bunnymeals: [] };

// ── Helpers ─────────────────────────────────────

function isFound(item) {
    if (item.type === 'alien')     return S.myAliens.some(a => a.alienId === item.id);
    if (item.type === 'toy')       return S.myToys.some(t => t.id === item.id);
    if (item.type === 'bunnymeal') return S.myBunnymeals.some(b => b.id === item.id);
    return false;
}

function canPull() {
    const banner = BANNERS[activeBanner];
    return S.pts >= CONFIG.GACHA_COST && !isPulling && banner.pool.length > 0;
}

// ── Banner switching ─────────────────────────────

function switchBanner(id) {
    if (!BANNERS[id] || activeBanner === id) return;
    activeBanner = id;

    // Update tab buttons
    document.querySelectorAll('.gacha-banner-btn').forEach(btn => btn.classList.remove('on'));
    const activeBtn = document.getElementById(`gbanner-${id}`);
    if (activeBtn) activeBtn.classList.add('on');

    // Update title
    const titleEl = document.getElementById('gacha-banner-title');
    if (titleEl) titleEl.textContent = BANNERS[id].title;

    // Reset last-pull panel to default
    const lastEl = document.getElementById('gacha-last-pull');
    if (lastEl) {
        lastEl.innerHTML = `<div class="gacha-last-lbl">LAST PULL</div><div class="gacha-last-empty">${BANNERS[id].emptyMsg}</div>`;
    }

    updateGachaDisplay();
    updateGachaHeader();
    renderRecents();
}

// ── Header / pts display ────────────────────────

function updateGachaHeader() {
    const valEl = document.getElementById('gacha-pts-val');
    if (valEl) valEl.textContent = S.pts;
    const btn = document.getElementById('gacha-pull-btn');
    if (btn) btn.disabled = !canPull();
}

function showPtsDelta(text) {
    const el = document.getElementById('gacha-pts-delta');
    if (!el) return;
    el.textContent = text;
    el.style.display = 'block';
    el.style.animation = 'none';
    void el.offsetWidth; // force reflow to restart animation
    el.style.animation = 'gPtsUp 1.1s forwards';
    setTimeout(() => { el.style.display = 'none'; }, 1100);
}

// ── Display case ────────────────────────────────

function updateGachaDisplay() {
    const caseEl = document.getElementById('gacha-case-rows');
    if (!caseEl) return;

    const displayItems = BANNERS[activeBanner].displayItems;

    // Empty banner — show a placeholder message
    if (!displayItems.length) {
        caseEl.innerHTML = `<div class="gc-empty-banner">
            <div class="gc-empty-icon">🍜</div>
            <div class="gc-empty-msg">Coming soon!</div>
            <div class="gc-empty-sub">New species on their way to the konbini.</div>
        </div>`;
        const foundEl = document.getElementById('gacha-found-text');
        if (foundEl) foundEl.textContent = '0/0 discovered';
        const dotsEl = document.getElementById('gacha-progress-dots');
        if (dotsEl) dotsEl.innerHTML = '';
        return;
    }

    const rows = [displayItems.slice(0, 3), displayItems.slice(3, 6), displayItems.slice(6, 9)].filter(r => r.length);
    caseEl.innerHTML = rows.map((row, ri) =>
        `<div class="gc-row${ri < rows.length - 1 ? ' gc-row-border' : ''}">${
            row.map((item, ci) => {
                const found = isFound(item);
                const rc = RARITY_RC[item.rarity] || RARITY_RC.common;
                const idx = ri * 3 + ci;
                const floatDur = (2.4 + idx * 0.15).toFixed(2);
                return `
                <div class="gc-slot${found ? ' gc-found' : ''}">
                    <div class="gc-img-wrap">
                        <img src="${item.src}" alt="${found ? item.name : '???'}"
                             style="filter:${found ? 'none' : 'brightness(0) opacity(.4)'};animation:${found ? `floatY ${floatDur}s ease-in-out infinite` : 'none'};">
                        ${found && item.rarity === 'rare' ? '<div class="gc-rare-badge">✦</div>' : ''}
                    </div>
                    <div class="gc-name">${found ? item.name : '???'}</div>
                    <div class="gc-stars">${found ? rc.stars : '·'}</div>
                </div>`;
            }).join('')
        }</div>`
    ).join('');

    const foundCount = displayItems.filter(isFound).length;
    const foundEl = document.getElementById('gacha-found-text');
    if (foundEl) foundEl.textContent = `${foundCount}/${displayItems.length} discovered`;

    const dotsEl = document.getElementById('gacha-progress-dots');
    if (dotsEl) {
        dotsEl.innerHTML = displayItems.map(item => {
            const f = isFound(item);
            const rare = item.rarity === 'rare';
            return `<div class="gc-dot${f ? ' on' : ''}${f && rare ? ' rare' : ''}"></div>`;
        }).join('');
    }
}

// ── Last pull panel ──────────────────────────────

function updateLastPull(prize) {
    const el = document.getElementById('gacha-last-pull');
    if (!el) return;
    let rarity, src, name;
    if (prize.type === 'alien')          { const a = ALIENS[prize.id]; rarity = a.rarity; src = a.img; name = a.name; }
    else if (prize.type === 'toy')       { rarity = 'common';   src = prize.img;       name = prize.name; }
    else if (prize.type === 'bunnymeal') { const b = BUNNYMEALS[prize.id]; rarity = b?.rarity || 'common'; src = b?.img || ''; name = b?.name || prize.id; }
    else                                 { rarity = 'uncommon'; src = '';              name = prize.label; }
    const rc = RARITY_RC[rarity] || RARITY_RC.common;
    el.innerHTML = `
        <div class="gacha-last-lbl">LAST PULL</div>
        <div class="gacha-last-item">
            <div class="gacha-last-thumb${rarity === 'rare' ? ' rare' : ''}">
                ${src ? `<img src="${src}" alt="${name}">` : '<span style="font-size:18px;">✦</span>'}
            </div>
            <div>
                <div class="gacha-last-name">${name}</div>
                <div class="gacha-last-stars">${rc.stars}</div>
            </div>
        </div>`;
}

// ── Recent pulls strip ───────────────────────────

function addToRecents(prize) {
    let rarity, src;
    if (prize.type === 'alien')          { const a = ALIENS[prize.id]; rarity = a.rarity; src = a.img; }
    else if (prize.type === 'toy')       { rarity = 'common';   src = prize.img; }
    else if (prize.type === 'bunnymeal') { const b = BUNNYMEALS[prize.id]; rarity = b?.rarity || 'common'; src = b?.img || ''; }
    else                                 { rarity = 'uncommon'; src = ''; }

    const history = pullHistory[activeBanner];
    pullHistory[activeBanner] = [{rarity, src}, ...history].slice(0, 10);

    renderRecents();
}

function renderRecents() {
    const el = document.getElementById('gacha-recents');
    if (!el) return;
    el.innerHTML = pullHistory[activeBanner].map(h => {
        return `<div class="gacha-recent-thumb${h.rarity === 'rare' ? ' rare' : ''}">
            ${h.src ? `<img src="${h.src}" alt="">` : '<span style="font-size:14px;">✦</span>'}
        </div>`;
    }).join('');
}

// ── Reveal card ──────────────────────────────────

function showRevealCard(inner, prize, dupe) {
    let rarity, src, name, typeLine;
    if (prize.type === 'alien')          { const a = ALIENS[prize.id]; rarity = a.rarity; src = a.img; name = a.name; typeLine = '👽 Alien Critter'; }
    else if (prize.type === 'toy')       { rarity = 'common';   src = prize.img;       name = prize.name;  typeLine = '🧸 Toy Item'; }
    else if (prize.type === 'bunnymeal') { const b = BUNNYMEALS[prize.id]; rarity = b?.rarity || 'common'; src = b?.img || ''; name = b?.name || prize.id; typeLine = '🍜 Bunnymeal'; }
    else                                 { rarity = 'uncommon'; src = '';              name = prize.label; typeLine = '✦ Subnick'; }
    const rc = RARITY_RC[rarity] || RARITY_RC.common;
    const isRare = rarity === 'rare' && !dupe;

    let sparkles = '';
    if (isRare) {
        for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * 360;
            const x = Math.round(Math.cos(angle * Math.PI / 180) * 60);
            const y = Math.round(Math.sin(angle * Math.PI / 180) * 60);
            sparkles += `<div class="gr-sparkle" style="transform:translate(calc(-50% + ${x}px),calc(-50% + ${y}px));animation-delay:${(i * 0.08).toFixed(2)}s">✦</div>`;
        }
    }

    const cardCls = `gr-card${dupe ? ' gr-dupe' : isRare ? ' gr-rare' : ''}`;

    inner.innerHTML = `
        <div class="${cardCls}">
            ${sparkles}
            ${dupe
                ? `<div class="gr-dupe-badge"><span class="gr-dupe-tag">DUPLICATE</span><span class="gr-dupe-pts">+${CONFIG.GACHA_DUPE_BACK} PTS</span></div>`
                : `<div class="gr-rarity-label">${rc.label}</div>`}
            <div class="gr-img-box">
                ${src ? `<img src="${src}" alt="${name}">` : `<div style="width:100px;height:100px;display:flex;align-items:center;justify-content:center;font-size:40px;">✦</div>`}
            </div>
            <div class="gr-name">${name}</div>
            <div class="gr-type">${typeLine}</div>
            ${dupe
                ? `<div class="gr-dupe-already">already in your collection</div>`
                : `<div class="gr-stars">${rc.stars}</div>`}
            <button class="gr-btn${dupe ? ' gr-btn-dupe' : ''}" onclick="closeGResult()">${dupe ? 'THANKS! 💛' : 'GOT IT! ★'}</button>
        </div>`;
}

// ── Pull logic ───────────────────────────────────

function pullGacha() {
    if (!canPull()) {
        if (S.pts < CONFIG.GACHA_COST) toast(`Need ${CONFIG.GACHA_COST} pts to pull! ✦`);
        return;
    }
    isPulling = true;

    addPts(-CONFIG.GACHA_COST, 'gacha:roll');
    if (window.triggerMission) window.triggerMission('gacha_roll');
    updateGachaHeader();
    SFX.play('gacha');

    // Machine shake
    const bodyEl = document.getElementById('gacha-machine-body');
    if (bodyEl) {
        bodyEl.classList.add('gacha-machine-shake');
        setTimeout(() => bodyEl.classList.remove('gacha-machine-shake'), 500);
    }

    // Weighted random from the active banner's pool
    const pool = BANNERS[activeBanner].pool;
    const total = pool.reduce((s, e) => s + e.weight, 0);
    let r = Math.random() * total;
    let prize = pool[pool.length - 1];
    for (const e of pool) { r -= e.weight; if (r <= 0) { prize = e; break; } }

    // Dupe detection (before applying rewards)
    let dupe = false;
    if (prize.type === 'alien')          dupe = S.myAliens.some(a => a.alienId === prize.id);
    else if (prize.type === 'toy')       dupe = S.myToys.some(t => t.id === prize.id);
    else if (prize.type === 'bunnymeal') dupe = S.myBunnymeals.some(b => b.id === prize.id);
    else if (prize.type === 'title')     dupe = S.unlockedNicks.includes(prize.id);

    // Phase 1: drop ball
    const overlay = document.getElementById('g-result');
    const inner   = document.getElementById('gr-inner');
    overlay.style.display = 'flex';
    inner.innerHTML = '<div class="gr-drop-ball"></div>';

    // Phase 2: opening GIF
    setTimeout(() => {
        inner.innerHTML = `
            <div class="gr-gif-wrap">
                <img class="gr-gif" src="assets/misc/gacha.gif?t=${Date.now()}" alt="opening">
                <div class="gr-opening-text">opening…</div>
            </div>`;
    }, 700);

    // Phase 3: reveal
    setTimeout(() => {
        // Apply rewards
        if (prize.type === 'alien') {
            const alien = ALIENS[prize.id];
            S.myAliens.push({
                uid: String(Date.now()),
                alienId: alien.id,
                name: alien.name,
                happiness: 80, hunger: 80, energy: 80,
                alive: true,
                lastFed: 0, lastPlayed: 0, lastPet: 0, lastSlept: 0,
            });
            if (S.myAliens.length >= 3) window.unlockNick('collector');
            if (alien.rarity === 'rare') window.unlockNick('rare');
            if (!dupe && window.triggerMission) window.triggerMission('new_alien');
            window.renderTamaSelect();
            window.updateProfileDisplay();
        } else if (prize.type === 'toy') {
            const existing = S.myToys.find(t => t.id === prize.id);
            if (existing) existing.count = (existing.count || 1) + 1;
            else S.myToys.push({id: prize.id, name: prize.name, desc: prize.desc, img: prize.img, count: 1});
            renderToys();
        } else if (prize.type === 'bunnymeal') {
            const bm = BUNNYMEALS[prize.id];
            if (!S.myBunnymeals.some(b => b.id === prize.id)) {
                S.myBunnymeals.push({ id: prize.id, name: bm?.name || prize.id, obtainedAt: Date.now() });
                if (window.triggerMission) window.triggerMission('new_bunny');
            }
        } else if (prize.type === 'title') {
            if (!dupe) window.unlockNick(prize.id);
        }

        // Refund for duplicates
        if (dupe) {
            addPts(CONFIG.GACHA_DUPE_BACK, 'gacha:dupe');
            showPtsDelta(`+${CONFIG.GACHA_DUPE_BACK}`);
        }

        updateGachaDisplay();
        updateGachaHeader();
        updateLastPull(prize);
        addToRecents(prize);
        scheduleSave();
        SFX.play('win');
        showRevealCard(inner, prize, dupe);
    }, 2600);
}

function closeGResult() {
    isPulling = false;
    const overlay = document.getElementById('g-result');
    const inner   = document.getElementById('gr-inner');
    if (overlay) overlay.style.display = 'none';
    if (inner)   inner.innerHTML = '';
    updateGachaHeader();
}

// ── Init (called by auth.js after login) ─────────

function buildGacha() {
    activeBanner = 'aliens';
    pullHistory = { aliens: [], bunnymeals: [] };
    document.querySelectorAll('.gacha-banner-btn').forEach(btn => btn.classList.remove('on'));
    const defaultBtn = document.getElementById('gbanner-aliens');
    if (defaultBtn) defaultBtn.classList.add('on');
    const titleEl = document.getElementById('gacha-banner-title');
    if (titleEl) titleEl.textContent = BANNERS.aliens.title;
    updateGachaDisplay();
    updateGachaHeader();
    renderRecents();
}

// ── Toys (used by Tama window) ───────────────────

function renderToys() {
    const el = document.getElementById('tama-toys');
    if (!el) return;
    if (!S.myToys.length || !S.selTama || !S.selTama.alive) {
        el.innerHTML = '';
        return;
    }
    el.innerHTML = `<div style="font-family:var(--font-display);font-size:var(--fs-xs);color:var(--pink);margin-bottom:6px;letter-spacing:0.5px;">🧸 TOYS</div>
  <div style="display:flex;gap:6px;flex-wrap:wrap;">${S.myToys.map(t => `
    <button onclick="giveToy('${t.id}')" style="display:flex;align-items:center;gap:6px;background:var(--cream);border:2.5px solid var(--dark);border-radius:var(--radius-sm);padding:4px 8px;font-family:var(--font-display);font-size:var(--fs-xs);cursor:pointer;box-shadow:0 2px 0 var(--dark);letter-spacing:0.5px;">
      <img src="${t.img}" style="width:22px;height:22px;object-fit:contain;" onerror="this.style.display='none'">
      ${t.name} ×${t.count || 1}
    </button>`).join('')}
  </div>`;
}

function giveToy(id) {
    if (!S.selTama || !S.selTama.alive) {
        toast('Select an alive alien first!');
        return;
    }
    const toy = S.myToys.find(t => t.id === id);
    if (!toy) return;
    S.selTama.happiness = Math.min(100, S.selTama.happiness + 50);
    toast(`${S.selTama.name} loved the ${toy.name}! ♡ +50 happiness`);
    toy.count = (toy.count || 1) - 1;
    if (toy.count <= 0) S.myToys = S.myToys.filter(t => t.id !== id);

    const charEl = document.getElementById('tama-char');
    if (charEl) {
        charEl.classList.remove('tpulse');
        void charEl.offsetWidth;
        charEl.classList.add('tpulse');
    }

    if (window.updateTamaDisplay) window.updateTamaDisplay();
    renderToys();
    scheduleSave();
}

window.buildGacha   = buildGacha;
window.pullGacha    = pullGacha;
window.closeGResult = closeGResult;
window.switchBanner = switchBanner;
window.renderToys   = renderToys;
window.giveToy      = giveToy;
